import format from 'date-fns/format';
import setMonth from 'date-fns/setMonth';
import setYear from 'date-fns/setYear';
import startOfMonth from 'date-fns/startOfMonth';
import isWithinInterval from 'date-fns/isWithinInterval';
import { KeyboardEvent, useEffect, useState } from 'react';
import { useDayPicker, useNavigation } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { Color } from '@tmh-website/shared/colors';
import { DropdownThemeColor } from '@tmh-website/ui/Dropdown';
import DropdownMenu from '@tmh-website/ui/DropdownMenu';
import DropdownMenuItem from '@tmh-website/ui/DropdownMenuItem';
import { SelectOption } from '@tmh-website/ui/FormElements';
import { SelectTextProps } from '@tmh-website/ui/SelectText';

import { CalendarThemeColor, calendarThemes } from './Calendar.theme';
import * as Styled from './CalendarDropdowns.styles';

interface CalendarDropdownProps
  extends Pick<SelectTextProps, 'name' | 'themeColor' | 'options' | 'value' | 'placeholder'> {
  onSelect: (name: SelectTextProps['name'], value: SelectTextProps['value']) => void;
  dropdownThemeColor: DropdownThemeColor;
}

const CalendarDropdown = ({
  themeColor = Color.Neutral_0_White,
  dropdownThemeColor,
  name,
  options,
  onSelect,
  value,
}: CalendarDropdownProps) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div>
      <DropdownMenu
        themeColor={dropdownThemeColor}
        anchorEl={
          <Styled.Button
            type='button'
            onKeyUp={(e: KeyboardEvent) => {
              if (e.key === 'Enter') {
                setIsVisible(!isVisible);
              }
            }}
            onClick={() => setIsVisible(!isVisible)}
            $themeColor={calendarThemes[themeColor].formElementsThemeColor}>
            {options.find(option => option.value === value)?.label}
            <svg width='12' height='12' viewBox='0 0 256 256' fill='none'>
              <path
                d='M208 96L128 176L48 96'
                stroke='currentColor'
                strokeWidth='32'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </Styled.Button>
        }>
        {options.map(option => (
          <DropdownMenuItem
            key={option.value}
            themeColor={dropdownThemeColor}
            disabled={option.disabled}
            onSelect={() => onSelect(name, option.value)}>
            {option.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenu>
    </div>
  );
};

interface CalendarDropdownsProps {
  themeColor: CalendarThemeColor;
  value?: Date;
}

const CalendarDropdowns = ({ themeColor, value }: CalendarDropdownsProps) => {
  const { fromDate, toDate, locale } = useDayPicker();
  const { goToMonth, currentMonth } = useNavigation();
  const { t } = useTranslation();

  const months: SelectOption[] = [];
  const years: SelectOption[] = [];
  const date = startOfMonth(new Date());
  for (let month = 0; month <= 11; month++) {
    const newDate = setMonth(date, month);
    months.push({ label: format(newDate, 'MMMM', { locale }), value: month });
  }

  const fromYear = fromDate?.getFullYear();
  const toYear = toDate?.getFullYear();
  if (fromYear && toYear) {
    for (let year = fromYear; year <= toYear; year++) {
      const yearOption = { label: year.toString(), value: year };
      years.push(yearOption);
    }
  }

  const onMonthSelect = (_name: string, month: string | readonly string[] | number | undefined) => {
    let newDate = setMonth(value ?? new Date(), month as number);

    newDate = setYear(newDate, currentMonth?.getFullYear?.());
    goToMonth(newDate);
  };

  const onYearSelect = (_name: string, year: string | readonly string[] | number | undefined) => {
    let newDate = setYear(value ?? new Date(), year as number);

    newDate = setMonth(newDate, currentMonth?.getMonth());
    goToMonth(newDate);
  };

  /**
   * Automatically sets the selected month to a valid one, if it is not within the allowed from / to date range.
   */
  useEffect(() => {
    if (fromDate && toDate && isWithinInterval(currentMonth, { start: fromDate, end: toDate }) === false) {
      const newDate = setMonth(toDate, currentMonth.getMonth());
      goToMonth(newDate);
    }
  }, [currentMonth]);

  return (
    <Styled.Container>
      <CalendarDropdown
        name='month'
        options={months}
        themeColor={calendarThemes[themeColor].formElementsThemeColor}
        dropdownThemeColor={calendarThemes[themeColor].dropdownsThemeColor}
        onSelect={onMonthSelect}
        placeholder={t('global:calendar.month') as string}
        value={currentMonth?.getMonth?.()}
      />
      <CalendarDropdown
        name='year'
        options={years}
        themeColor={calendarThemes[themeColor].formElementsThemeColor}
        dropdownThemeColor={calendarThemes[themeColor].dropdownsThemeColor}
        onSelect={onYearSelect}
        placeholder={t('global:calendar.year') as string}
        value={currentMonth?.getFullYear?.()}
      />
    </Styled.Container>
  );
};

export default CalendarDropdowns;
